import { useEffect } from 'react';
import gql from 'graphql-tag';
import { useMutation, useQuery } from 'react-apollo';
import { useWizardContext } from 'pages/qualify/provider';
import { Inputs, Organization, StepIdsEnum } from 'pages/qualify/types';
import { parseAnyListToOptionsList } from 'pages/qualify/utils';
import { WIZARD_IMAGES_PER_STEP_ID } from 'pages/qualify/wizard-images-urls';
import moment from 'moment';
import WizardStep from '../wizard-step';
import WizardInput from '../wizard-input';

export default function SeminarsStep() {
  const { tokenData, watch, setValues, values, token, organizationData } =
    useWizardContext();
  const {
    data: applicationAssetQueryData,
    loading,
    refetch,
  } = useQuery<{
    sponsorshipApplicationView: Partial<Organization>;
  }>(APPLICATION_ASSET_QUERY, {
    variables: { season: tokenData.season, year: tokenData.year },
    context: { headers: { Authorization: `Bearer ${token}` } },
    fetchPolicy: 'network-only',
  });
  const [performMutation] = useMutation(MUTATION);
  const accepted = watch('accepted');

  const modifyInputsBeforeMutation = (
    data: Partial<Inputs>,
  ): Partial<Inputs> => ({
    ...data,
    possibleAttendance: Number(data.possibleAttendance),
  });

  useEffect(() => {
    if (values.organizationId) {
      const { savedStepId } = organizationData?.sponsorshipApplication;
      if (savedStepId === StepIdsEnum.SEMINAR) return;
      const { seminarDetail } =
        applicationAssetQueryData?.sponsorshipApplicationView
          .sponsorshipApplication ?? {};
      setValues({
        ...values,
        accepted:
          applicationAssetQueryData?.sponsorshipApplicationView
            .sponsorshipApplication?.seminarAccepted ?? false,
        description: seminarDetail?.capabilityDetailable?.description,
        possibleAttendance:
          seminarDetail?.capabilityDetailable.possibleAttendance,
        eventDate: seminarDetail?.capabilityDetailable.eventDate,
        sponsorablePropertyIds:
          seminarDetail?.associatedSponsorableProperties?.map(
            (property) => property.id,
          ),
        refusalReason:
          applicationAssetQueryData?.sponsorshipApplicationView
            .sponsorshipApplication?.seminarRefusalReason,
      });
    }
  }, [values.organizationId, applicationAssetQueryData]);

  return (
    <WizardStep
      performMutation={performMutation}
      refetchAfterMutation={refetch}
      mutationStaticIds={{
        organizationId: tokenData.id,
        sponsorshipApplicationId:
          applicationAssetQueryData?.sponsorshipApplicationView
            ?.sponsorshipApplication?.id,
        offerId: tokenData.offer_id,
        id: applicationAssetQueryData?.sponsorshipApplicationView
          .sponsorshipApplication?.seminarDetail?.capabilityDetailable?.id,
      }}
      modifyInputsBeforeMutation={modifyInputsBeforeMutation}
      isLoading={loading}
    >
      <WizardStep.Picture
        src={WIZARD_IMAGES_PER_STEP_ID[StepIdsEnum.SEMINAR]}
      />
      <WizardStep.Content
        title="Seminars"
        subtitle="Seminars are scheduled as part of existing organization events where a sponsor representative is invited to speak on a sports health-related topic."
      >
        <div className="sui-grid" style={{ gap: 24 }}>
          <WizardInput
            type="boolean-question"
            name="accepted"
            label="My organization has scheduled parent, coaches, board meetings, or other similar events this season that could include a sponsor seminar"
            helpText="Average sponsorship funding $100-$120"
          />
          {accepted && (
            <>
              <WizardInput
                type="date"
                name="eventDate"
                label="Approximate date of event"
                rules={{
                  validate: {
                    todayOrAfterToday: (value) => {
                      const pickedDate = moment(value);
                      const today = moment().startOf('day');
                      if (pickedDate.isBefore(today)) {
                        return 'Approximate date of event must be today or later';
                      }
                      return true;
                    },
                  },
                }}
              />
              <WizardInput
                type="number"
                name="possibleAttendance"
                label="Anticipated number of event attendees"
              />
              <WizardInput
                type="textarea"
                name="description"
                label="Description of event"
              />
              <WizardInput
                type="select"
                isMulti
                name="sponsorablePropertyIds"
                label="Programs this applies to"
                rules={{ required: 'Please select at least one program' }}
                options={parseAnyListToOptionsList(
                  values.sponsorableProperties ?? [],
                  { withEmptyOption: false },
                )}
              />
            </>
          )}
          {!accepted && (
            <>
              <WizardInput
                type="textarea"
                name="refusalReason"
                label="If possible, please share your reasons and specify the amount of funding at which you might reconsider opting in to this asset"
              />
            </>
          )}
        </div>
      </WizardStep.Content>
    </WizardStep>
  );
}

const APPLICATION_ASSET_QUERY = gql`
  query SponsorshipApplicationView($season: String!, $year: Int!) {
    sponsorshipApplicationView {
      id
      sponsorshipApplication(season: $season, year: $year) {
        id
        seminarAccepted
        seminarRefusalReason
        seminarDetail {
          id
          associatedSponsorableProperties {
            id
            name
          }
          capabilityDetailable {
            ... on Seminar {
              id
              description
              eventDate
              possibleAttendance
            }
          }
        }
      }
    }
  }
`;

const MUTATION = gql`
  mutation upsertSeminar($input: UpsertSeminarInput!) {
    upsertSeminar(input: $input) {
      id
    }
  }
`;
